import { AlertTriangle } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";

/**
 * 「不编排,直接出片」知情确认。
 *
 * ═══ 为什么从 VideoCreatePage 抽出来 ══════════════════════════════════════════
 * 2026-08-19 老板走查:他点了「不编排,直接出片」,弹窗跳出来,他点右下角那颗紫色实心按钮,
 * **结果走了编排**。真机量到的坐标:「仍直接出片」x=489 无背景灰字 /
 * 「先 AI 智能编排(推荐)」x=789 紫色实心,坐在标准确认对话框的**确认位**。
 * 任何人的肌肉记忆都是「右下角实心 = 确认我刚才那个操作」。
 *
 * 2026-08-06 那次改动的出发点是对的(旧版把「我知道,仍直接生成」做成主按钮 = 把坏选择做成
 * 视觉主选项),但**矫枉过正**成了另一头:它现在会把已经明确表达了意图的用户劫走。
 * 于是 0819 反转成「确认位 = 用户已经表达的意图,次级位 = 我们的建议」。
 *
 * ═══ 2026-09-12 老板拍板:再转回来(现行) ═══════════════════════════════════════
 * 0819 那版落到屏幕上是这样的:整段正文都在劝「不编排通常出废片、积分照扣」,右下角却把
 * 最亮的一颗实心按钮给了「直接出片」—— **视觉权重与推荐语义正好颠倒**。
 * 现行口径:推荐项(先 AI 智能编排)= 确认位 + 实心主按钮;「直接出片(~N 积分)」= 左侧
 * ghost。0819 的教训靠**位置**兜住:直发那颗仍在,不藏、不改文案、不改回调,点得到。
 *
 * 抽成独立组件还为了**测得到**:VideoCreatePage 2300+ 行,那边的既有手法是源码锁(grep
 * 字面量),对「哪一颗是视觉主按钮」这种问题钉不准。
 */
export interface BareSendConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 用户坚持直发(确认位)。 */
  onConfirmBareSend: () => void;
  /** 改走编排(次级出口)。 */
  onGoCompose: () => void;
  bareSendCredits: number;
  bareSendDurationSec: number;
  /** 用户选的时长档在直发路上出不来时给档名(如「中」);否则 null。 */
  clampedTier: string | null;
  /** 该档的目标秒数(15/30/45),配合 clampedTier 说清差距。 */
  tierTargetSec?: number;
}

export function BareSendConfirmDialog({
  open,
  onOpenChange,
  onConfirmBareSend,
  onGoCompose,
  bareSendCredits,
  bareSendDurationSec,
  clampedTier,
  tierTargetSec,
}: BareSendConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>直接出片?这段文案会原样发给视频模型</AlertDialogTitle>
          <AlertDialogDescription>
            不经导演编排 = 没有分镜、没有逐镜口播、没有跨镜一致性,短文案直发通常出废片,
            <span className="font-medium text-foreground">
              而积分照扣(本次约 {bareSendCredits} 积分,按单镜 {bareSendDurationSec}s 计)
            </span>
            。先「AI 智能编排」多花的只是台本图的钱,出片质量差别很大。
            <br />
            适合直发的情况:你粘的就是一段写好的单镜画面描述,只想快速看一眼效果。
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/**
         * ★时长档冲突是这个弹窗里**最该被看见**的一句,必须有自己的视觉权重。
         *   线上它是正文里的一个 `text-warning-foreground` span —— 真机实测在当前皮肤下
         *   与正文同色,整段读过去根本注意不到(老板选了 30s 走直发拿到 15s,就是这么发生的)。
         *   独立警示块 + role="alert",既有视觉权重也让读屏念出来。
         */}
        {clampedTier && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              你选的「{clampedTier}」时长档在这条路上<b>出不来</b> —— 直发是单镜一次生成,最长{" "}
              {bareSendDurationSec}s。要 {tierTargetSec}s 的成片得走「AI
              智能编排」,由它拆成多镜再拼。
            </span>
          </div>
        )}

        {/**
         * DOM 顺序 = 视觉左右(footer 是 sm:justify-between):次级在左,确认位在右。
         *
         * ★2026-09-12 老板拍板:**推荐项占确认位且是实心主按钮**,花钱的那颗降为 ghost 放左边。
         *   0819 那版把「直接出片」提成实心主按钮,理由是「确认位 = 用户已表达的意图」——
         *   但落到屏幕上就成了:整段正文在劝「别这么干」,右下角却给了它最亮的一颗按钮,
         *   视觉与推荐语义正好颠倒。劝返要靠文案**和**视觉权重一起说同一句话。
         * ★只换视觉与位置:两颗的 data-testid、文案、onClick 回调一个没动
         *   (直发仍走 onConfirmBareSend,编排仍走 onGoCompose;接线锁见
         *   `videoCreateBareSendWiring.test.ts`)。
         */}
        <AlertDialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground"
            data-testid="video-bare-send-confirm"
            onClick={onConfirmBareSend}
          >
            直接出片(~{bareSendCredits} 积分)
          </Button>
          <AlertDialogAction data-testid="video-bare-send-compose" onClick={onGoCompose}>
            还是先 AI 智能编排(推荐)
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
